import React, { createContext, useCallback, useState, useContext } from "react";
import { PaymentMethod } from "../models/payment";
import { Customer, CustomerWallet } from "../models/Service";

interface PaymentContextType {
  currentStep: number;
  customerUser: Customer | null;
  setCustomerUser: React.Dispatch<React.SetStateAction<Customer | null>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  amount: string;
  setAmount: React.Dispatch<React.SetStateAction<string>>;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  selectedMethod: PaymentMethod | null;
  setSelectedMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  setSelectedWallet: React.Dispatch<
    React.SetStateAction<CustomerWallet | null>
  >;
  selectedWallet: CustomerWallet | null;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  // The answers to the service's own fields. Held here rather than in the
  // template so they survive the move to the outcome page and back.
  configuredValues: Record<string, string>;
  setConfiguredValues: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  // The outcome of a payment this journey has already sent. While it is
  // set the journey's steps are not shown again: a Back onto Confirm would
  // otherwise offer a live Pay for a payment that is already in flight.
  submittedOutcome: SubmittedOutcome | null;
  setSubmittedOutcome: React.Dispatch<
    React.SetStateAction<SubmittedOutcome | null>
  >;
  reset: () => void;
}

export type SubmittedOutcome = {
  to: string;
  state?: Record<string, unknown>;
};

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

// Null outside a journey, e.g. an outcome page opened from a bare link.
export const useOptionalPayment = () => useContext(PaymentContext);

// A provider nested inside another reuses the outer one. The journey scope
// mounts it above the service and outcome routes; the page's own mount then
// adds nothing, and still stands alone where no scope exists.
export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const outer = useContext(PaymentContext);
  if (outer) return <>{children}</>;
  return <PaymentState>{children}</PaymentState>;
};

const PaymentState: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [customerUser, setCustomerUser] = useState<Customer | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null
  );
  const [selectedWallet, setSelectedWallet] = useState<CustomerWallet | null>(
    null
  );
  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [configuredValues, setConfiguredValues] = useState<
    Record<string, string>
  >({});
  const [submittedOutcome, setSubmittedOutcome] =
    useState<SubmittedOutcome | null>(null);

  const reset = useCallback(() => {
    setCurrentStep(1);
    setAmount("");
    setName("");
    setEmail("");
    setCustomerUser(null);
    setSelectedMethod(null);
    setSelectedWallet(null);
    setError("");
    setPhoneNumber("");
    setConfiguredValues({});
    setSubmittedOutcome(null);
  }, []);

  return (
    <PaymentContext.Provider
      value={{
        customerUser,
        setCustomerUser,
        currentStep,
        setCurrentStep,
        amount,
        setAmount,
        name,
        setName,
        email,
        setEmail,
        selectedMethod,
        setSelectedMethod,
        phoneNumber,
        setPhoneNumber,
        error,
        setError,
        selectedWallet,
        setSelectedWallet,
        configuredValues,
        setConfiguredValues,
        submittedOutcome,
        setSubmittedOutcome,
        reset,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
